'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetProposalRow } from '@morfi/backend-sdk-v2/client';
import { hashDiagnosticEntity, useAuth } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import { MARKET_LIFECYCLE_MODAL_SHOWN, onEvent } from '@/events';
import { prefetchRouteFull } from '@/hooks/use-intent-route-prefetch';
import { isRoutePayloadResponse, isRoutePrefetchResponse, LAUNCH_PREFETCH_MAX_WAIT_MS, launchPrefetchDelayMs, type RouteResourceEntry } from '@/lib/launchpad/proposal-launch';
import { seenKey } from '@/lib/market/lifecycle-modal-eligibility';
import { useAppRouter } from '@/lib/network/use-app-router';
import { logger } from '@/lib/telemetry/logger';
import { fireResolutionFireworks } from '@/lib/ui/confetti';
import { useDemoMode } from '@/providers/demo-mode-provider';
import { liveGraduationKey, useMarketLifecycleModalStore } from '@/stores/market-lifecycle-modal';
import { useMarketLifecycleSeenStore } from '@/stores/market-lifecycle-seen';
import { ProposalGraduatedMarketMissing } from './proposal-graduated-market-missing';
interface ProposalGraduationRedirectProps {
  proposal: Pick<GetProposalRow, 'graduatedMarketPubkey' | 'proposer' | 'viewerShareBalance'>;
  walletAddress: string | undefined;
  /** This page saw the proposal backing before it went live (not a cold visit). */
  watchedGoLive: boolean;
}

/**
 * Hands a live proposal off to its market. The funded proposal stays on screen
 * (the page renders it launching) while the market route FULL-prefetches; once
 * that response lands the celebration opens and the page replaces onto the
 * prefetched market with no loading skeleton. Renders nothing itself.
 */
export function ProposalGraduationRedirect(t0) {
  const $ = _c(15);
  const {
    proposal,
    walletAddress,
    watchedGoLive
  } = t0;
  const marketPubkey = proposal.graduatedMarketPubkey;
  const {
    userId: creatorUserId,
    walletAddress: creatorWalletAddress
  } = proposal.proposer;
  const viewerShareBalance = proposal.viewerShareBalance;
  const router = useAppRouter();
  const {
    isDemoMode
  } = useDemoMode();
  const {
    user,
    isAuthenticated,
    isLoading
  } = useAuth();
  const handledMarket = useRef(null);
  const isCreator = isAuthenticated && (user?.id !== undefined && creatorUserId === user.id || walletAddress !== undefined && walletAddress === creatorWalletAddress);
  const isBacker = isAuthenticated && BigInt(viewerShareBalance ?? "0") > BigInt(0);
  const variant = isCreator ? "graduation-creator" : isBacker ? "graduation-backer" : "graduation-generic";
  const viewerKey = walletAddress ?? user?.id ?? "anonymous";
  const celebrationViewer = isAuthenticated ? user?.id ?? viewerKey : "anonymous";
  const celebrateRef = useRef(_temp);
  let t1;
  if ($[0] !== celebrationViewer || $[1] !== isBacker || $[2] !== isDemoMode || $[3] !== marketPubkey || $[4] !== variant || $[5] !== viewerKey) {
    t1 = () => {
      celebrateRef.current = () => {
        if (!marketPubkey) {
          return;
        }
        useMarketLifecycleSeenStore.getState().markSeen(seenKey(viewerKey, marketPubkey, "graduation"));
        const celebrationKey = liveGraduationKey(celebrationViewer, marketPubkey, isDemoMode);
        const opened = useMarketLifecycleModalStore.getState().openLiveGraduation(celebrationKey, {
          marketPubkey,
          stage: "graduation",
          variant,
          isBacker,
          winnerOutcomeIndex: null,
          winningOutcomeLabel: null,
          winningOutcomeImageUrl: null,
          winningOutcomeImageNsfw: false,
          claim: null
        });
        if (opened) {
          fireResolutionFireworks();
          onEvent(MARKET_LIFECYCLE_MODAL_SHOWN, {
            marketPubkey,
            stage: "graduation",
            variant
          });
        }
      };
    };
    $[0] = celebrationViewer;
    $[1] = isBacker;
    $[2] = isDemoMode;
    $[3] = marketPubkey;
    $[4] = variant;
    $[5] = viewerKey;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t1);
  let t2;
  let t3;
  if ($[7] !== isLoading || $[8] !== marketPubkey || $[9] !== router || $[10] !== watchedGoLive) {
    t2 = () => {
      if (!marketPubkey || isLoading || handledMarket.current === marketPubkey) {
        return;
      }
      handledMarket.current = marketPubkey;
      const path = `/markets/${marketPubkey}`;
      const prefetchDelayMs = launchPrefetchDelayMs(watchedGoLive);
      let handedOff = false;
      let prefetchedAt = Number.POSITIVE_INFINITY;
      let responses = 0;
      const observer = observeResources(entries => {
        const prefetched = entries.filter(entry => entry.startTime >= prefetchedAt && isRoutePrefetchResponse(entry.name, path));
        responses = responses + prefetched.length;
        responses;
        if (prefetched.some(entry_0 => isRoutePayloadResponse(entry_0, path))) {
          handOff(true);
        }
      });
      const prefetch = setTimeout(() => {
        prefetchedAt = performance.now();
        prefetchRouteFull(router, path);
      }, prefetchDelayMs);
      const maxWait = setTimeout(() => handOff(false), prefetchDelayMs + LAUNCH_PREFETCH_MAX_WAIT_MS);
      function handOff(prefetchLanded) {
        if (handedOff || !marketPubkey) {
          return;
        }
        handedOff = true;
        observer?.disconnect();
        clearTimeout(maxWait);
        if (!prefetchLanded) {
          logger.warn("proposal.launch_prefetch_timed_out", {
            marketHash: hashDiagnosticEntity(marketPubkey),
            responses,
            observed: observer !== null
          });
        }
        celebrateRef.current();
        router.replace(path, {
          scroll: false
        });
      }
      return () => {
        clearTimeout(prefetch);
        clearTimeout(maxWait);
        observer?.disconnect();
        if (!handedOff) {
          handledMarket.current = null;
        }
      };
    };
    t3 = [isLoading, marketPubkey, router, watchedGoLive];
    $[7] = isLoading;
    $[8] = marketPubkey;
    $[9] = router;
    $[10] = watchedGoLive;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  let t4;
  if ($[13] !== marketPubkey) {
    t4 = marketPubkey ? null : <ProposalGraduatedMarketMissing />;
    $[13] = marketPubkey;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function _temp() {}
interface ObservedResource extends RouteResourceEntry {
  readonly startTime: number;
}

/**
 * Reports finished resource requests, or `null` where the browser cannot: some
 * engines throw on the `type` form of `observe`. The handoff then relies on its
 * wait cap instead of stalling on a disabled "Creating market..." button.
 */
function observeResources(onEntries: (entries: ReadonlyArray<ObservedResource>) => void): PerformanceObserver | null {
  if (typeof PerformanceObserver === 'undefined') return null;
  const observer = new PerformanceObserver(list => onEntries(list.getEntries().map((entry): ObservedResource => ({
    name: entry.name,
    startTime: entry.startTime,
    encodedBodySize: 'encodedBodySize' in entry && typeof entry.encodedBodySize === 'number' ? entry.encodedBodySize : 0
  }))));
  try {
    observer.observe({
      type: 'resource'
    });
    return observer;
  } catch (error) {
    logger.warn('proposal.launch_resource_observer_unavailable', {
      message: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
}